import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Network, NetworkCountry, NetworksByMncMccResponse, NetworksByMncMccSearchDto } from '../models/network.model';
import { RestUtils } from './rest-utils';
import ISO6391 from 'iso-639-1';

@Injectable()
export class NetInfoService {
  utils = new RestUtils();

  headers = new HttpHeaders({
    'Content-Type': ['application/json'],
  });

  constructor(public http: HttpClient) {}

  networks(params?: AllRequestParamsNetwork): Observable<Network[]> {
    const queryParams: any = {};
    queryParams.regionName = params?.regionName;
    queryParams.networkName = params?.networkName;
    queryParams.light = params?.light;
    queryParams.sort = params?.sort;
    const url = this.utils.buildUrl('ROLE/net-intel/networks', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<Network[]>(url, options);
  }

  networksByMccMnc(search: NetworksByMncMccSearchDto): Observable<Network[]> {
    const url = this.utils.buildUrl('ROLE/net-intel/networks/search/by-mcc-mnc-list');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<NetworksByMncMccResponse>(url, search, options).pipe(map(response => response.searchDtos.map(d => d.network)));
  }

  network(id: number): Observable<Network> {
    const url = this.utils.buildUrl(`ROLE/net-intel/networks/${id}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<Network>(url, options);
  }

  createNetwork(): Network {
    return {
      id: null,
      name: '',
      mcc: '',
      mnc: '',
      country: null,
      prefixes: [],
    };
  }

  saveNetwork(network: Network) {
    const url = this.utils.buildUrl(network.id ? `ROLE/net-intel/networks/${network.id}` : `ROLE/net-intel/networks`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    if (network.id) {
      return this.http.put<Network>(url, network, options);
    }
    return this.http.post<Network>(url, network, options);
  }

  deleteNetwork(id: number) {
    const url = this.utils.buildUrl(`ROLE/net-intel/networks/${id}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.delete(url, options);
  }

  countries(params?: AllRequestParamsRegion) {
    const queryParams: any = {};
    if (params) {
      if (params.regionName) {
        queryParams.regionName = params.regionName;
      }
      if (params.prefixes) {
        queryParams.prefixes = params.prefixes;
      }
      if (params.sort && params.sort.length) {
        queryParams.sort = params.sort;
      }
    }
    const url = this.utils.buildUrl('ROLE/net-intel/countries', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<NetworkCountry[]>(url, options).pipe(
      map(countries => {
        return countries.map(country => ({
          ...country,
          languageName: country.languageCode ? ISO6391.getName(country.languageCode) : 'N/A',
        }));
      })
    );
  }

  country(id: number): Observable<NetworkCountry> {
    const url = this.utils.buildUrl(`ROLE/net-intel/countries/${id}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<NetworkCountry>(url, options);
  }

  saveCountry(country: NetworkCountry) {
    const url = this.utils.buildUrl(country.id ? `ROLE/net-intel/countries/${country.id}` : `ROLE/net-intel/countries`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    if (country.id) {
      return this.http.put<NetworkCountry>(url, country, options);
    }
    return this.http.post<NetworkCountry>(url, country, options);
  }

  deleteCountry(id: number) {
    const url = this.utils.buildUrl(`ROLE/net-intel/countries/${id}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.delete<NetworkCountry>(url, options);
  }

  countryForNumber(number: string): Observable<NetworkCountry> {
    const url = this.utils.buildUrl(`ROLE/net-intel/countries/search/by-phone-number`, { phoneNumber: number });
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.get<NetworkCountry>(url, options);
  }

  createCountry(): NetworkCountry {
    return {
      id: null,
      name: '',
      isoAlpha2: '',
      prefixes: [],
      languageCode: 'en',
    };
  }
}

export class AllRequestParamsNetwork {
  search: string;
  sort: string[] = [];
  light = false;
  regionName = '';
  networkName = '';

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}

export class AllRequestParamsRegion {
  search: string;

  sort: string[] = [];

  regionName = '';

  prefixes = '';

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}
